/**
 * Évolution inter-années (2017-2018 → année courante)
 * KPIs : fidélisation, finalistes, nouveaux, perte
 * Lecture seule depuis tous les schémas `annee_*`
 */
import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, Users, GraduationCap, UserPlus, UserMinus,
  Loader2, Filter, Info, Printer,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_URL } from '../../services/baseApi';
import { useGetPolesQuery } from '../../services/polesOptionsApi';

// Charge un asset PNG public en data-URL pour jsPDF.addImage
async function chargerLogoBase64(src: string): Promise<string | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// Nettoie un texte pour jsPDF (helvetica par défaut ne supporte que latin-1 ;
// les caractères Unicode hors table impriment des glyphes étranges type "!'").
function safeTxt(s: string | number | undefined | null): string {
  if (s === undefined || s === null) return '';
  return String(s)
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/↔/g, '<->')
    .replace(/⇒/g, '=>')
    .replace(/÷/g, '/')
    .replace(/×/g, 'x')
    .replace(/…/g, '...')
    .replace(/[“”«»]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/—/g, '-')
    .replace(/–/g, '-')
    .replace(/•/g, '*')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=');
}

interface BreakdownItem {
  libelle: string;
  effectif_depuis: number;
  effectif_vers: number;
  retenus: number;
  finalistes: number;
  nouveaux: number;
  perdus: number;
  taux_fidelisation: number;
  taux_nouveaux: number;
  croissance_pct: number;
}

interface TransitionItem {
  depuis: string;
  vers: string;
  effectif_depuis: number;
  effectif_vers: number;
  retenus: number;
  finalistes: number;
  nouveaux: number;
  perdus: number;
  taux_fidelisation: number;
  taux_perte: number;
  taux_nouveaux: number;
  croissance_pct: number;
  par_niveau?: BreakdownItem[];
  par_pole?: BreakdownItem[];
}

interface EvolutionResponse {
  filtres: { pole: string; type_formation: string; niveau_finaliste: string };
  annees: string[];
  effectifs: number[];
  transitions: TransitionItem[];
  kpis: {
    nb_annees_analysees: number;
    annee_min: string;
    annee_max: string;
    effectif_initial: number;
    effectif_final: number;
    croissance_globale_pct: number;
    fidelisation_moyenne: number;
    perte_moyenne: number;
    renouvellement_moyen: number;
    total_finalistes: number;
    total_nouveaux: number;
    total_perdus: number;
  };
  detail?: string;
}

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'amber';
}> = ({ label, value, sub, icon, color }) => {
  const colors: Record<string, string> = {
    blue:   'border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 text-blue-700 dark:text-blue-300',
    green:  'border-green-200 dark:border-green-800 bg-green-50/40 dark:bg-green-900/10 text-green-700 dark:text-green-300',
    orange: 'border-orange-200 dark:border-orange-800 bg-orange-50/40 dark:bg-orange-900/10 text-orange-700 dark:text-orange-300',
    red:    'border-red-200 dark:border-red-800 bg-red-50/40 dark:bg-red-900/10 text-red-700 dark:text-red-300',
    purple: 'border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10 text-purple-700 dark:text-purple-300',
    amber:  'border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-900/10 text-amber-700 dark:text-amber-300',
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[color]}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold">
        {icon}{label}
      </div>
      <div className="mt-2 text-xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
};

const EvolutionAnnuelle: React.FC = () => {
  const [pole, setPole] = useState('');
  const [typeFormation, setTypeFormation] = useState<'FI' | 'DD' | 'FI_DD' | 'CS' | 'TOUS'>('FI_DD');
  const [niveauFinaliste, setNiveauFinaliste] = useState<'M2' | 'L3'>('M2');
  const [data, setData] = useState<EvolutionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Export PDF — design sobre, identité institutionnelle 2i
  const exportPDF = async () => {
    if (!data || data.annees.length === 0) return;
    setPdfLoading(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const w = doc.internal.pageSize.getWidth();
      const h = doc.internal.pageSize.getHeight();
      const marginX = 15;
      const NAVY: [number, number, number] = [30, 64, 124];
      const GRAY: [number, number, number] = [120, 120, 120];

      // ── En-tête : logo + titre ─────────────────────────────────
      const logo = await chargerLogoBase64('/logo.png');
      if (logo) {
        try { doc.addImage(logo, 'PNG', marginX, 10, 18, 18); } catch { /* ignore */ }
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...NAVY);
      doc.text(safeTxt('INSTITUT INTERNATIONAL 2i'), logo ? marginX + 22 : marginX, 16);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
      doc.text(safeTxt('Excellence et Innovation'), logo ? marginX + 22 : marginX, 21);

      // Titre principal centré
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...NAVY);
      doc.text(safeTxt('Évolution inter-années'), w / 2, 36, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
      doc.text(
        safeTxt('Fidélisation, finalistes, nouveaux étudiants - analyse comparée'),
        w / 2, 42, { align: 'center' },
      );

      // Filets de séparation
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(marginX, 30, w - marginX, 30);
      doc.line(marginX, 46, w - marginX, 46);

      // ── Bloc filtres / période ─────────────────────────────────
      const filtresLignes: string[] = [];
      filtresLignes.push(
        safeTxt(`Période : ${data.kpis.annee_min || '-'}  ->  ${data.kpis.annee_max || '-'}`
        + `   (${data.kpis.nb_annees_analysees} années)`)
      );
      const sf = data.filtres || {} as any;
      const lblTF: Record<string, string> = {
        FI_DD: 'Formation Initiale + Double Diplôme', FI: 'Formation Initiale',
        DD: 'Double Diplôme', CS: 'Cours du Soir', TOUS: 'Toutes formations',
      };
      filtresLignes.push(
        safeTxt(`Type de formation : ${lblTF[sf.type_formation] || sf.type_formation || '-'}`
        + (sf.pole ? `   |   Pôle : ${sf.pole}` : '   |   Pôle : tous')
        + (sf.niveau_finaliste ? `   |   Niveau finaliste : ${sf.niveau_finaliste}` : ''))
      );
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      let y = 53;
      for (const l of filtresLignes) { doc.text(l, marginX, y); y += 5; }
      y += 3;

      // ── KPIs globaux ──────────────────────────────────────────
      const titreSection = (label: string, largeur = 45) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...NAVY);
        doc.text(safeTxt(label), marginX, y);
        y += 4;
        doc.setDrawColor(...NAVY);
        doc.setLineWidth(0.5);
        doc.line(marginX, y, marginX + largeur, y);
      };

      titreSection('Indicateurs globaux', 40);
      y += 4;
      const kpis = data.kpis;
      const kpiData: string[][] = [
        ['Effectif initial', `${kpis.effectif_initial}`, `année ${kpis.annee_min}`],
        ['Effectif actuel', `${kpis.effectif_final}`,
          `${kpis.croissance_globale_pct >= 0 ? '+' : ''}${kpis.croissance_globale_pct.toFixed(1)}% global`],
        ['Fidélisation moyenne', `${kpis.fidelisation_moyenne.toFixed(1)} %`, 'par transition'],
        ['Renouvellement moyen', `${kpis.renouvellement_moyen.toFixed(1)} %`,
          `${kpis.total_nouveaux} nouveaux cumulés`],
        ['Total finalistes', `${kpis.total_finalistes}`,
          `${niveauFinaliste} sortis (toutes années)`],
        ['Perte moyenne', `${kpis.perte_moyenne.toFixed(1)} %`,
          `${kpis.total_perdus} étudiants perdus`],
      ].map(r => r.map(safeTxt));
      autoTable(doc, {
        startY: y, body: kpiData, theme: 'plain',
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { textColor: [80, 80, 80], cellWidth: 55 },
          1: { fontStyle: 'bold', textColor: NAVY, cellWidth: 25, halign: 'right' },
          2: { textColor: GRAY, fontStyle: 'italic' },
        },
        didDrawCell: (d) => {
          if (d.row.index < kpiData.length - 1) {
            doc.setDrawColor(235, 235, 235); doc.setLineWidth(0.1);
            const yy = d.cell.y + d.cell.height;
            doc.line(d.cell.x, yy, d.cell.x + d.cell.width, yy);
          }
        },
      });
      y = (doc as any).lastAutoTable.finalY + 8;

      // ── Graphes vectoriels (effectifs + transitions) ──────────
      // Saut de page si moins de 110mm restant (les 2 graphes prennent ~95mm)
      if (y > h - 115) { doc.addPage(); y = 20; }

      // Helper : dessine un axe + une polyline d'évolution des effectifs
      const dessinerCourbeEffectifs = (
        x0: number, y0: number, largeur: number, hauteur: number,
        annees: string[], effectifs: number[],
      ) => {
        if (annees.length === 0) return;
        const padL = 18, padB = 12, padT = 8, padR = 6;
        const chartW = largeur - padL - padR;
        const chartH = hauteur - padT - padB;
        const maxVal = Math.max(...effectifs, 10);
        const niceMax = Math.ceil(maxVal / 100) * 100 || 100;
        // Axe Y
        doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.2);
        doc.line(x0 + padL, y0 + padT, x0 + padL, y0 + padT + chartH);
        doc.line(x0 + padL, y0 + padT + chartH, x0 + padL + chartW, y0 + padT + chartH);
        // Graduations Y (4 niveaux)
        doc.setFontSize(6.5); doc.setTextColor(140, 140, 140);
        for (let g = 0; g <= 4; g++) {
          const val = Math.round((niceMax / 4) * g);
          const yy = y0 + padT + chartH - (g / 4) * chartH;
          doc.setDrawColor(235, 235, 235); doc.setLineWidth(0.1);
          doc.line(x0 + padL, yy, x0 + padL + chartW, yy);
          doc.text(String(val), x0 + padL - 1, yy + 1.5, { align: 'right' });
        }
        // Points + lignes
        const stepX = chartW / Math.max(annees.length - 1, 1);
        const pts: Array<[number, number]> = annees.map((_, i) => {
          const px = x0 + padL + i * stepX;
          const py = y0 + padT + chartH - (effectifs[i] / niceMax) * chartH;
          return [px, py];
        });
        doc.setDrawColor(...NAVY); doc.setLineWidth(0.6);
        for (let i = 0; i < pts.length - 1; i++) {
          doc.line(pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1]);
        }
        // Points + labels valeurs
        doc.setFillColor(...NAVY);
        doc.setFontSize(6.5);
        for (let i = 0; i < pts.length; i++) {
          doc.circle(pts[i][0], pts[i][1], 1.2, 'F');
          doc.setTextColor(...NAVY);
          doc.text(String(effectifs[i]), pts[i][0], pts[i][1] - 2.5, { align: 'center' });
        }
        // Labels X
        doc.setFontSize(6.5); doc.setTextColor(80, 80, 80);
        for (let i = 0; i < annees.length; i++) {
          doc.text(safeTxt(annees[i]), x0 + padL + i * stepX, y0 + padT + chartH + 4, { align: 'center' });
        }
      };

      // Helper : barres groupées Retenus/Nouveaux/Finalistes/Perdus par transition
      const dessinerBarresTransitions = (
        x0: number, y0: number, largeur: number, hauteur: number,
        transitions: TransitionItem[],
      ) => {
        if (transitions.length === 0) return;
        const padL = 18, padB = 14, padT = 6, padR = 4;
        const chartW = largeur - padL - padR;
        const chartH = hauteur - padT - padB;
        const cats = ['retenus', 'nouveaux', 'finalistes', 'perdus'] as const;
        const colors: Record<string, [number, number, number]> = {
          retenus: [34, 197, 94],     // green-500
          nouveaux: [59, 130, 246],   // blue-500
          finalistes: [139, 92, 246], // violet-500
          perdus: [239, 68, 68],      // red-500
        };
        const maxVal = Math.max(
          ...transitions.flatMap(t => cats.map(c => t[c] as number)),
          10,
        );
        const niceMax = Math.ceil(maxVal / 100) * 100 || 100;
        // Axes
        doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.2);
        doc.line(x0 + padL, y0 + padT, x0 + padL, y0 + padT + chartH);
        doc.line(x0 + padL, y0 + padT + chartH, x0 + padL + chartW, y0 + padT + chartH);
        // Grilles + labels Y
        doc.setFontSize(6.5); doc.setTextColor(140, 140, 140);
        for (let g = 0; g <= 4; g++) {
          const val = Math.round((niceMax / 4) * g);
          const yy = y0 + padT + chartH - (g / 4) * chartH;
          doc.setDrawColor(235, 235, 235); doc.setLineWidth(0.1);
          doc.line(x0 + padL, yy, x0 + padL + chartW, yy);
          doc.text(String(val), x0 + padL - 1, yy + 1.5, { align: 'right' });
        }
        // Barres groupées : pour chaque transition, 4 barres adjacentes
        const groupeW = chartW / transitions.length;
        const barW = (groupeW * 0.8) / cats.length;
        const gapInter = (groupeW * 0.2) / 2;
        transitions.forEach((t, ti) => {
          cats.forEach((c, ci) => {
            const val = t[c] as number;
            const bx = x0 + padL + ti * groupeW + gapInter + ci * barW;
            const bH = (val / niceMax) * chartH;
            const by = y0 + padT + chartH - bH;
            const col = colors[c];
            doc.setFillColor(...col);
            doc.rect(bx, by, barW * 0.92, bH, 'F');
          });
          // Label X (court : N->N+1)
          const lbl = `${t.depuis.split('-')[0]}→${t.vers.split('-')[1]}`;
          doc.setFontSize(6); doc.setTextColor(80, 80, 80);
          doc.text(
            safeTxt(lbl),
            x0 + padL + ti * groupeW + groupeW / 2,
            y0 + padT + chartH + 4,
            { align: 'center' },
          );
        });
        // Légende en bas
        doc.setFontSize(6.5);
        const legendY = y0 + padT + chartH + 9;
        const legendItems = [
          ['Retenus', colors.retenus],
          ['Nouveaux', colors.nouveaux],
          ['Finalistes', colors.finalistes],
          ['Perdus', colors.perdus],
        ] as Array<[string, [number, number, number]]>;
        let lx = x0 + padL;
        for (const [lbl, col] of legendItems) {
          doc.setFillColor(...col);
          doc.rect(lx, legendY - 2, 3, 2, 'F');
          doc.setTextColor(60, 60, 60);
          doc.text(safeTxt(lbl), lx + 4, legendY);
          lx += doc.getTextWidth(lbl) + 10;
        }
      };

      // Titre + 2 graphes côte à côte (ou empilés selon largeur)
      titreSection('Visualisation', 30);
      y += 4;
      const colW = (w - 2 * marginX - 4) / 2;
      const chartH = 55;
      // Sous-titres
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(safeTxt('Évolution des effectifs'), marginX, y + 3);
      doc.text(safeTxt('Transitions année par année'), marginX + colW + 4, y + 3);
      doc.setFont('helvetica', 'normal');
      // Cadres légers
      doc.setDrawColor(230, 230, 230); doc.setLineWidth(0.2);
      doc.rect(marginX, y + 5, colW, chartH);
      doc.rect(marginX + colW + 4, y + 5, colW, chartH);
      dessinerCourbeEffectifs(marginX, y + 5, colW, chartH, data.annees, data.effectifs);
      dessinerBarresTransitions(marginX + colW + 4, y + 5, colW, chartH, data.transitions);
      y += chartH + 12;

      // ── Tableau récap transitions ─────────────────────────────
      if (y > h - 60) { doc.addPage(); y = 20; }
      titreSection('Synthèse des transitions');
      autoTable(doc, {
        startY: y + 3,
        head: [['Transition', 'Eff. N', 'Eff. N+1', 'Retenus', 'Nouveaux', 'Final.', 'Perdus', 'Fidel.', 'Croiss.']],
        body: data.transitions.map((t) => [
          safeTxt(`${t.depuis} -> ${t.vers}`),
          String(t.effectif_depuis), String(t.effectif_vers),
          String(t.retenus), String(t.nouveaux), String(t.finalistes), String(t.perdus),
          `${t.taux_fidelisation.toFixed(1)}%`,
          `${t.croissance_pct >= 0 ? '+' : ''}${t.croissance_pct.toFixed(1)}%`,
        ]),
        theme: 'grid',
        styles: {
          fontSize: 8, cellPadding: 2,
          lineColor: [200, 200, 200], lineWidth: 0.15,
        },
        headStyles: {
          fillColor: NAVY, textColor: 255, fontStyle: 'bold', fontSize: 8,
          halign: 'center', lineColor: [200, 200, 200], lineWidth: 0.15,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { halign: 'left' },
          1: { halign: 'right' }, 2: { halign: 'right' },
          3: { halign: 'right', textColor: [22, 101, 52] },
          4: { halign: 'right', textColor: [37, 99, 235] },
          5: { halign: 'right', textColor: [126, 34, 206] },
          6: { halign: 'right', textColor: [153, 27, 27] },
          7: { halign: 'right', fontStyle: 'bold' },
          8: { halign: 'right', fontStyle: 'bold' },
        },
        didParseCell: (d) => {
          // Header centré sur toutes les colonnes
          if (d.section === 'head') {
            d.cell.styles.halign = 'center';
          }
          if (d.section === 'body' && d.column.index === 8) {
            const raw = String(d.cell.raw || '');
            d.cell.styles.textColor = raw.startsWith('-') ? [153, 27, 27] : [22, 101, 52];
          }
        },
      });
      y = (doc as any).lastAutoTable.finalY + 8;

      // ── Helper : tableau breakdown réutilisable ────────────────
      const rendreBreakdown = (
        items: BreakdownItem[] | undefined, libelleColonne: string,
      ) => {
        if (!items || items.length === 0) return;
        // Saut de page si trop bas
        if (y > h - 60) { doc.addPage(); y = 20; }
        autoTable(doc, {
          startY: y,
          head: [[libelleColonne, 'Eff. N', 'Eff. N+1', 'Retenus', 'Nouv.', 'Final.', 'Perdus', 'Fidel.', 'Croiss.']],
          body: items.map(g => [
            safeTxt(g.libelle),
            String(g.effectif_depuis), String(g.effectif_vers),
            String(g.retenus), String(g.nouveaux), String(g.finalistes), String(g.perdus),
            `${g.taux_fidelisation.toFixed(1)}%`,
            `${g.croissance_pct >= 0 ? '+' : ''}${g.croissance_pct.toFixed(1)}%`,
          ]),
          theme: 'grid',
          styles: {
            fontSize: 7.5, cellPadding: 1.5,
            lineColor: [210, 210, 210], lineWidth: 0.12,
          },
          headStyles: {
            fillColor: [55, 65, 81], textColor: 255, fontSize: 7.5, fontStyle: 'bold',
            halign: 'center', lineColor: [210, 210, 210], lineWidth: 0.12,
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'right' }, 2: { halign: 'right' },
            3: { halign: 'right', textColor: [22, 101, 52] },
            4: { halign: 'right', textColor: [37, 99, 235] },
            5: { halign: 'right', textColor: [126, 34, 206] },
            6: { halign: 'right', textColor: [153, 27, 27] },
            7: { halign: 'right', fontStyle: 'bold' },
            8: { halign: 'right', fontStyle: 'bold' },
          },
          didParseCell: (d) => {
            if (d.section === 'head') {
              d.cell.styles.halign = 'center';
            }
            if (d.section === 'body' && d.column.index === 8) {
              const raw = String(d.cell.raw || '');
              d.cell.styles.textColor = raw.startsWith('-') ? [153, 27, 27] : [22, 101, 52];
            }
          },
        });
        y = (doc as any).lastAutoTable.finalY + 5;
      };

      // ── Pour chaque transition : breakdowns par niveau et par pôle ──
      for (const t of data.transitions) {
        // Saut de page si trop bas (header transition + 2 tableaux mini)
        if (y > h - 70) { doc.addPage(); y = 20; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...NAVY);
        doc.text(safeTxt(`Transition ${t.depuis} -> ${t.vers}`), marginX, y);
        y += 3;
        doc.setDrawColor(...NAVY); doc.setLineWidth(0.5);
        doc.line(marginX, y, marginX + 50, y);
        y += 4;

        if (t.par_niveau && t.par_niveau.length > 0) {
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(60, 60, 60);
          doc.text('Par niveau', marginX, y);
          y += 2;
          rendreBreakdown(t.par_niveau, 'Niveau');
        }
        if (t.par_pole && t.par_pole.length > 0) {
          if (y > h - 50) { doc.addPage(); y = 20; }
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(60, 60, 60);
          doc.text('Par pôle', marginX, y);
          y += 2;
          rendreBreakdown(t.par_pole, 'Pôle');
        }
        y += 3;
      }

      // Légende / méthodologie (en bas ou nouvelle page)
      if (y > h - 30) { doc.addPage(); y = 20; }
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...GRAY);
      const meth = [
        safeTxt("Méthode : matricule comme clé unique inter-années (lecture seule)."),
        safeTxt("Fidélisation = retenus / (effectif N - finalistes).  Renouvellement = nouveaux / effectif N+1."),
        safeTxt(`Finaliste = étudiant en ${niveauFinaliste} qui n'est plus inscrit l'année suivante.`),
        safeTxt("Les breakdowns par niveau et par pôle sont basés sur l'année source (N) pour retenus/finalistes/perdus, et sur l'année suivante (N+1) pour les nouveaux."),
      ];
      for (const m of meth) { doc.text(m, marginX, y); y += 3.5; }

      // ── Pied de page (toutes les pages) ────────────────────────
      const totalPages = doc.getNumberOfPages();
      const dateStr = new Date().toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.2);
        doc.line(marginX, h - 12, w - marginX, h - 12);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...GRAY);
        doc.text(safeTxt(`Généré le ${dateStr}`), marginX, h - 8);
        doc.text(safeTxt('Institut International 2i - Rapport interne'), w / 2, h - 8, { align: 'center' });
        doc.text(`Page ${p} / ${totalPages}`, w - marginX, h - 8, { align: 'right' });
      }

      const periode = `${kpis.annee_min}_${kpis.annee_max}`.replace(/-/g, '');
      doc.save(`Evolution_inter-annees_${periode}.pdf`);
    } finally {
      setPdfLoading(false);
    }
  };

  const { data: poles = [] } = useGetPolesQuery({});

  const charger = async () => {
    setLoading(true);
    setError(null);
    try {
      const usp = new URLSearchParams();
      if (pole) usp.set('pole', pole);
      usp.set('type_formation', typeFormation);
      usp.set('niveau_finaliste', niveauFinaliste);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';
      const res = await fetch(`${API_URL}/stats/evolution-annuelle/?${usp.toString()}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Données pour les charts
  const chartEffectifs = data
    ? data.annees.map((a, i) => ({ annee: a, effectif: data.effectifs[i] }))
    : [];

  const chartTransitions = data
    ? data.transitions.map((t) => ({
        transition: `${t.depuis.split('-')[0]}→${t.vers.split('-')[1]}`,
        retenus: t.retenus,
        nouveaux: t.nouveaux,
        finalistes: t.finalistes,
        perdus: t.perdus,
      }))
    : [];

  return (
    <div className="p-4 max-w-screen-2xl mx-auto space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <TrendingUp size={20} className="text-blue-600" />
              Évolution inter-années
            </h1>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Fidélisation, finalistes, nouveaux étudiants — analyse comparée d'une année à l'autre.
              <span className="block text-[11px] mt-0.5 italic">
                Lecture seule sur les schémas existants. Aucune écriture.
              </span>
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
              Pôle
            </label>
            <select
              value={pole}
              onChange={(e) => setPole(e.target.value)}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les pôles</option>
              {poles.map((p: any) => (
                <option key={p.id} value={p.nom}>{p.nom}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
              Type de formation
            </label>
            <select
              value={typeFormation}
              onChange={(e) => setTypeFormation(e.target.value as any)}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="FI_DD">FI + DD</option>
              <option value="FI">FI uniquement</option>
              <option value="DD">DD uniquement</option>
              <option value="CS">Cours du Soir</option>
              <option value="TOUS">Tous</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
              Niveau finaliste
            </label>
            <select
              value={niveauFinaliste}
              onChange={(e) => setNiveauFinaliste(e.target.value as any)}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="M2">M2 (Master)</option>
              <option value="L3">L3 (Licence)</option>
            </select>
          </div>
          <button
            onClick={charger}
            disabled={loading}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded inline-flex items-center gap-1.5"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Filter size={14} />}
            {loading ? 'Calcul…' : 'Analyser'}
          </button>
          {data && data.annees.length > 0 && (
            <button
              onClick={exportPDF}
              disabled={pdfLoading}
              className="px-4 py-1.5 bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white text-sm font-medium rounded inline-flex items-center gap-1.5"
              title="Imprimer / télécharger en PDF"
            >
              {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
              {pdfLoading ? 'Génération…' : 'Imprimer PDF'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data?.detail && !data.transitions.length && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded p-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <span>{data.detail}</span>
        </div>
      )}

      {data && data.annees.length > 0 && (
        <>
          {/* KPIs globaux */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard
              label="Années"
              value={data.kpis.nb_annees_analysees}
              sub={`${data.kpis.annee_min} → ${data.kpis.annee_max}`}
              icon={<Users size={13} />}
              color="blue"
            />
            <KpiCard
              label="Effectif initial"
              value={data.kpis.effectif_initial}
              sub={data.kpis.annee_min}
              icon={<Users size={13} />}
              color="purple"
            />
            <KpiCard
              label="Effectif actuel"
              value={data.kpis.effectif_final}
              sub={
                <span className={data.kpis.croissance_globale_pct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {data.kpis.croissance_globale_pct >= 0 ? '+' : ''}{data.kpis.croissance_globale_pct.toFixed(1)}% global
                </span> as any
              }
              icon={data.kpis.croissance_globale_pct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              color={data.kpis.croissance_globale_pct >= 0 ? 'green' : 'red'}
            />
            <KpiCard
              label="Fidélisation moy."
              value={`${data.kpis.fidelisation_moyenne.toFixed(1)}%`}
              sub="par transition"
              icon={<Users size={13} />}
              color="green"
            />
            <KpiCard
              label="Renouvellement"
              value={`${data.kpis.renouvellement_moyen.toFixed(1)}%`}
              sub={`${data.kpis.total_nouveaux} nouveaux cumulés`}
              icon={<UserPlus size={13} />}
              color="amber"
            />
            <KpiCard
              label="Finalistes"
              value={data.kpis.total_finalistes}
              sub={`${niveauFinaliste} sortis (toutes années)`}
              icon={<GraduationCap size={13} />}
              color="purple"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3">
                Évolution des effectifs
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartEffectifs}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="annee" stroke="#6b7280" fontSize={11} />
                  <YAxis stroke="#6b7280" fontSize={11} />
                  <Tooltip />
                  <Line
                    type="monotone" dataKey="effectif"
                    stroke="#3b82f6" strokeWidth={2.5}
                    dot={{ r: 4, fill: '#3b82f6' }}
                    name="Effectif"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3">
                Transitions année par année
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartTransitions}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="transition" stroke="#6b7280" fontSize={11} />
                  <YAxis stroke="#6b7280" fontSize={11} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="retenus"    stackId="a" fill="#22c55e" name="Retenus" />
                  <Bar dataKey="nouveaux"   stackId="a" fill="#3b82f6" name="Nouveaux" />
                  <Bar dataKey="finalistes" fill="#8b5cf6" name="Finalistes" />
                  <Bar dataKey="perdus"     fill="#ef4444" name="Perdus" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tableau détaillé */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <h3 className="px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
              Détail des transitions
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 text-[11px] uppercase text-gray-600 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2 text-left">Transition</th>
                    <th className="px-3 py-2 text-right">Effectif N</th>
                    <th className="px-3 py-2 text-right">Effectif N+1</th>
                    <th className="px-3 py-2 text-right text-green-700 dark:text-green-300">Retenus</th>
                    <th className="px-3 py-2 text-right text-blue-700 dark:text-blue-300">Nouveaux</th>
                    <th className="px-3 py-2 text-right text-purple-700 dark:text-purple-300">Finalistes</th>
                    <th className="px-3 py-2 text-right text-red-700 dark:text-red-300">Perdus</th>
                    <th className="px-3 py-2 text-right">Fidélisation</th>
                    <th className="px-3 py-2 text-right">Perte</th>
                    <th className="px-3 py-2 text-right">Croissance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.transitions.map((t) => (
                    <tr key={`${t.depuis}-${t.vers}`} className="text-gray-800 dark:text-gray-200">
                      <td className="px-3 py-2 font-medium">{t.depuis} → {t.vers}</td>
                      <td className="px-3 py-2 text-right font-mono">{t.effectif_depuis}</td>
                      <td className="px-3 py-2 text-right font-mono">{t.effectif_vers}</td>
                      <td className="px-3 py-2 text-right font-mono text-green-700 dark:text-green-300">{t.retenus}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-700 dark:text-blue-300">{t.nouveaux}</td>
                      <td className="px-3 py-2 text-right font-mono text-purple-700 dark:text-purple-300">{t.finalistes}</td>
                      <td className="px-3 py-2 text-right font-mono text-red-700 dark:text-red-300">{t.perdus}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{t.taux_fidelisation.toFixed(2)}%</td>
                      <td className="px-3 py-2 text-right font-mono">{t.taux_perte.toFixed(2)}%</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${
                        t.croissance_pct >= 0
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-red-700 dark:text-red-300'
                      }`}>
                        {t.croissance_pct >= 0 ? '+' : ''}{t.croissance_pct.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[11px] text-gray-500 dark:text-gray-400 italic">
            Fidélisation = retenus / (effectif N − finalistes). Renouvellement = nouveaux / effectif N+1.
            Finaliste = étudiant en {niveauFinaliste} qui n'est plus inscrit l'année suivante.
            Perdu = étudiant non finaliste qui ne se réinscrit pas (drop-out).
          </div>
        </>
      )}

      {!data && !loading && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-4 text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
          <Info size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            Sélectionnez les filtres puis cliquez sur « Analyser » pour calculer l'évolution
            inter-années. Le calcul lit tous les schémas <code>annee_*</code> existants en
            lecture seule — aucune modification n'est effectuée.
          </span>
        </div>
      )}
    </div>
  );
};

export default EvolutionAnnuelle;
